import { ShieldCheck } from "lucide-react";
import LegalPageLayout from "../components/LegalPageLayout";

const sections = [
  {
    id: "overview",
    title: "1. Overview & scope",
    body: [
      "EXOPILOT provides a service-quality intelligence platform consisting of a cloud console, an on-premise processing server operated by your organisation, and wearable pendant devices paired with a companion phone application. This policy explains what data each layer processes, where that data lives, and the guarantees built into the architecture.",
      "This policy applies to the EXOPILOT cloud console and the synchronisation interfaces between the console and customer-operated local servers. The on-premise server and the recordings it processes remain under the exclusive control of the deploying organisation.",
    ],
  },
  {
    id: "on-premise",
    title: "2. The on-premise principle",
    body: [
      "EXOPILOT is designed around a single architectural rule: voice recordings never leave your premises. Audio captured by pendant devices is relayed over Bluetooth Low Energy to the companion phone app and forwarded only to your organisation's local server, where an offline evaluation engine scores each interaction against your standard operating procedures.",
      "The uplink from your local server to the cloud carries structured, numeric quality scores only. No audio files, waveforms, or transcripts are transmitted to, stored in, or accessible from the EXOPILOT cloud — by design, not by configuration.",
    ],
  },
  {
    id: "cloud-data",
    title: "3. Data processed in the cloud",
    body: [
      "The cloud console processes the following categories of data on behalf of your organisation: administrator account details (name, company, work email, hashed password); employee records you create (name, work email, department, position); device registry data (pendant serial numbers, companion app identifiers, battery level and last-seen timestamps); SOP documents you upload; numeric quality scores and session metadata reported by your local server; and synchronisation logs.",
      "Passwords are hashed with bcrypt and are never stored or transmitted in plain text. Session authentication uses short-lived, httpOnly, secure cookies.",
    ],
  },
  {
    id: "local-data",
    title: "4. Data processed only on your premises",
    body: [
      "Audio recordings of customer interactions, any intermediate transcripts, and raw model outputs are processed exclusively on your organisation's local server. EXOPILOT has no technical means of retrieving this material: the cloud exposes no endpoint capable of receiving audio, and the sync protocol validates payloads to numeric score structures only.",
      "Retention, encryption at rest, and deletion of on-premise audio are governed by your organisation's own policies and applicable local law.",
    ],
  },
  {
    id: "sync",
    title: "5. How synchronisation works",
    body: [
      "Downlink (cloud → local): SOP documents, employee–device mappings, and configuration are fetched by your local server over an authenticated, key-protected channel.",
      "Uplink (local → cloud): your local server pushes validated, bounded numeric scores (0–100) per employee session, plus device health telemetry (battery percentage, last-seen time). Every sync event is written to an audit log visible in your console.",
    ],
  },
  {
    id: "retention",
    title: "6. Retention & deletion",
    body: [
      "Employee records, scores, and SOPs are retained for as long as your workspace is active. Deleting an employee permanently removes their score history from the cloud. Deleting an SOP removes both its metadata and the stored document file. You may request full workspace deletion at any time, after which all associated cloud data is removed.",
    ],
  },
  {
    id: "security",
    title: "7. Security measures",
    body: [
      "Transport encryption (TLS) on all console and sync traffic; bcrypt password hashing; short-lived access tokens with httpOnly, secure cookies; brute-force lockout on repeated failed sign-ins; key-authenticated sync endpoints; strict tenant isolation so one organisation's data is never visible to another; and server-side validation on every write. For the full picture, see our Security & Trust Center.",
    ],
  },
  {
    id: "access",
    title: "8. Access control",
    body: [
      "Each company workspace is isolated by tenant. Console access requires authenticated administrator credentials belonging to that workspace. Local servers authenticate with a dedicated sync key that grants access only to the sync interfaces — never to console administration.",
    ],
  },
  {
    id: "transparency",
    title: "9. Employee transparency & consent",
    body: [
      "EXOPILOT is a workplace quality tool, and deploying organisations are responsible for lawful use. We require customers to inform employees that service interactions are recorded and evaluated, obtain any consent required by local employment and privacy law, and make evaluation criteria (the SOPs) available to affected staff. The per-employee score view is designed to support fair, criteria-based coaching — not covert surveillance.",
    ],
  },
  {
    id: "subprocessors",
    title: "10. Hosting & sub-processors",
    body: [
      "The cloud console is hosted on managed cloud infrastructure. We do not sell data, do not use customer data to train models, and do not share workspace data with third parties except infrastructure providers strictly necessary to operate the service.",
    ],
  },
  {
    id: "rights",
    title: "11. Your rights",
    body: [
      "Depending on your jurisdiction, you and your employees may have rights to access, correct, export, or erase personal data. Administrators can exercise most of these directly in the console (employee deletion, SOP deletion). For anything else, contact us and we will respond within 30 days.",
    ],
  },
  {
    id: "changes",
    title: "12. Changes to this policy",
    body: [
      "We will notify workspace administrators by email and console notice at least 30 days before material changes to this policy take effect.",
    ],
  },
  {
    id: "contact",
    title: "13. Contact",
    body: [
      "Privacy questions, data requests, and security reports: privacy@exopilot.io",
    ],
  },
];

export default function Privacy() {
  return (
    <LegalPageLayout
      testId="privacy"
      eyebrowIcon={ShieldCheck}
      eyebrow="Privacy Policy"
      title="Your voice data stays where it belongs."
      accent="With you."
      updated="June 2026"
      sections={sections}
      contactLine="Questions about how EXOPILOT handles data?"
      contactEmail="privacy@exopilot.io"
    />
  );
}
